import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Square, MapPin, Clock, DollarSign, Package, Volume2, VolumeX, ChevronLeft, CheckCircle, Navigation, Home, Building2, Car, Hand, Crosshair, Signal, Zap, CreditCard, AlertCircle, AlertTriangle, X, Camera, Upload, Image, Lock, WifiOff, Wifi, CloudOff, RefreshCw, Smartphone } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Polygon, Marker, Circle, useMap } from 'react-leaflet';
import { GoogleMap, useJsApiLoader, Polygon as GPolygon, Marker as GMarker, Polyline as GPolyline, Circle as GCircle } from '@react-google-maps/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TrackPageGoogleMap from '../components/map/TrackPageGoogleMap';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, sessionsAPI, mapCorrectionsAPI } from '../services/api';
import { useOfflineMode } from '../hooks/useOfflineMode';
import { cacheJobHouses, getCachedJobHouses } from '../services/offlineStorage';
import backgroundLocationService from '../services/backgroundLocation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for current position
const currentPositionIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #f97316, #dc2626);
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 10px rgba(249, 115, 22, 0.5);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Custom marker icon for house locations - small house icon
const houseIcon = new L.DivIcon({
  className: 'house-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  </div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Delivered house marker - yellow circle with black dot
const deliveredHouseIcon = new L.DivIcon({
  className: 'delivered-marker',
  html: `<div style="
    width: 18px;
    height: 18px;
    background: #fbbf24;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(251, 191, 36, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <div style="
      width: 6px;
      height: 6px;
      background: #000;
      border-radius: 50%;
    "></div>
  </div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Manual delivery marker - purple house icon for manual tracking mode
const manualDeliveryIcon = new L.DivIcon({
  className: 'manual-delivery-marker',
  html: `<div style="
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #a855f7;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(168, 85, 247, 0.5);
  ">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    </svg>
  </div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Component to update map view when position changes
const MapUpdater = ({ position, bounds, shouldFollow }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && !shouldFollow) {
      map.fitBounds(bounds, { padding: [30, 30] });
    } else if (position && shouldFollow) {
      map.setView(position, 17); // Zoom in closer when tracking
    }
  }, [position, bounds, shouldFollow, map]);
  return null;
};

// GPS Configuration for maximum accuracy
const GPS_CONFIG = {
  enableHighAccuracy: true,  // Use GPS, not WiFi/Cell
  maximumAge: 0,             // Always get fresh position, never cached
  timeout: 10000,            // 10 second timeout
};

// Ping interval in milliseconds (5 seconds for high accuracy as requested)
const PING_INTERVAL = 5000;

// Minimum distance threshold in meters to record a new point (reduces noise)
const MIN_DISTANCE_THRESHOLD = 2; // 2 meters

// Auto-delivery detection settings
const HOUSE_PROXIMITY_METERS = 10; // Distance to consider "at front door" of a house
const STOP_DURATION_SECONDS = 6; // Seconds stopped at front door to trigger delivery credit
const MOVEMENT_THRESHOLD_METERS = 2; // Movement under this is considered "stopped"

const TrackPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [deliveries, setDeliveries] = useState(0);
  const [earnings, setEarnings] = useState(0);
  const [distance, setDistance] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState('houses');
  const [saving, setSaving] = useState(false);
  
  // Server-side auto-save state
  const [lastServerSave, setLastServerSave] = useState(null);
  const [serverSaving, setServerSaving] = useState(false);
  const serverSaveIntervalRef = useRef(null);
  
  // Offline mode hook
  const {
    online,
    syncing,
    pendingCount,
    hasPendingData: hasOfflineData,
    saveGPSPoint,
    queueSession,
    syncPendingData,
    saveTrackingState,
    restoreTrackingState,
    clearTrackingState,
    mergeOfflineRoute,
    offlineGPSBuffer,
    wakeLockActive,
    requestWakeLock,
    releaseWakeLock
  } = useOfflineMode();
  
  // Photo upload state
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(null);
  const photoInputRef = useRef(null);
  const REQUIRED_PHOTOS = 10;
  
  // Job state
  const [currentJob, setCurrentJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  
  // GPS tracking states
  const [currentPosition, setCurrentPosition] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [gpsError, setGpsError] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [gpsSignalStrength, setGpsSignalStrength] = useState('searching');
  
  // Auto-delivery detection states
  const [houseLocations, setHouseLocations] = useState([]); // All houses in zone
  const [deliveredHouses, setDeliveredHouses] = useState(new Set()); // IDs of delivered houses
  // Houses already covered by OTHER workers / previous jobs — display-only.
  // NEVER merged into deliveredHouses: submitting them as this worker's own
  // work duplicated other workers' deliveries into their tracking doc
  // (prod E5: 7 workers claimed 3,781 house-credits in a 1,574-house zone).
  const [preDeliveredHouses, setPreDeliveredHouses] = useState(new Set());
  const [nearbyHouse, setNearbyHouse] = useState(null); // Currently nearby house
  const [stopTimer, setStopTimer] = useState(0); // Seconds stopped near house
  const [loadingHouses, setLoadingHouses] = useState(false);
  const [housesFetchAttempted, setHousesFetchAttempted] = useState(false); // Prevent repeated fetch attempts
  const [frontDoorDetectionEnabled, setFrontDoorDetectionEnabled] = useState(true); // GPS front door detection
  const [manualTrackingMode, setManualTrackingMode] = useState(false); // Manual delivery counting mode
  const [manualDeliveryLocations, setManualDeliveryLocations] = useState([]); // GPS locations of manual deliveries
  const [policyWarning, setPolicyWarning] = useState(null); // {distanceFt, count} far-from-door warning popup
  const flaggedMarksRef = useRef(0); // flagged marks this session
  
  // Background tracking state
  const [isBackgroundSupported, setIsBackgroundSupported] = useState(false);
  const [usingBackgroundTracking, setUsingBackgroundTracking] = useState(false);
  
  // Incomplete job warning states
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [showPartialDeliveryMsg, setShowPartialDeliveryMsg] = useState(false);
  
  // Out of zone warning state
  const [showOutOfZoneWarning, setShowOutOfZoneWarning] = useState(false);
  const [isOutsideZone, setIsOutsideZone] = useState(false);
  const outOfZoneCountRef = useRef(0); // Count consecutive out-of-zone readings
  const lastZoneWarningRef = useRef(null); // Prevent spamming warnings
  
  // Resume session state (for recovering from app close/refresh)
  const [showResumeSessionModal, setShowResumeSessionModal] = useState(false);
  const [savedSessionData, setSavedSessionData] = useState(null);
  
  // Refs for tracking
  const watchIdRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const lastPositionRef = useRef(null);
  const stopTimerRef = useRef(null);
  const lastMovementPositionRef = useRef(null);
  const audioRef = useRef(null);
  const offlineSaveIntervalRef = useRef(null);
  const liveLocationIntervalRef = useRef(null);
  
  // Check if background tracking is supported on mount
  useEffect(() => {
    setIsBackgroundSupported(backgroundLocationService.isBackgroundSupported());
    if (backgroundLocationService.isBackgroundSupported()) {
      console.log('[TrackPage] Background location tracking available on', backgroundLocationService.getPlatform());
    }
  }, []);
  
  // Default job for when not logged in or no job available
  const defaultJob = {
    id: 'google-maps-loader',
    title: 'Demo Delivery Zone',
    client: 'Demo Client',
    deliveries: 100,
    payPerFlyer: 0.50,
    totalPay: 50.00,
    mapCenter: [40.7128, -74.0060],
    deliveryBoundary: [
      [40.7100, -74.0100], [40.7100, -74.0020], [40.7160, -74.0020], [40.7160, -74.0100]
    ]
  };

  // Fetch user's active job from API
  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoadingJob(true);
        // Get user's active job
        const activeJobResponse = await jobsAPI.getMyActiveJob();
        
        if (activeJobResponse.data) {
          const job = activeJobResponse.data;
          // Extract zone label from title if present (e.g., "Pizza Delivery - Zone A1" -> "A1")
          const zoneLabelMatch = job.title.match(/Zone\s+([A-Z]\d+)/i);
          const zoneLabel = zoneLabelMatch ? zoneLabelMatch[1] : null;
          
          setCurrentJob({
            id: job.id,
            title: job.title,
            client: job.client,
            deliveries: job.deliveries,
            payPerFlyer: job.pay_per_flyer,
            totalPay: job.total_pay,
            bonusPay: job.bonus_pay || 0,
            mapCenter: job.map_center,
            deliveryBoundary: job.delivery_boundary,
            zoneLabel: zoneLabel,
            deliveryType: job.delivery_type || 'houses',
            deliveredHouseIds: job.delivered_house_ids || [],  // Pre-delivered houses (for partial jobs)
            pickupLocation: job.pickup_location,
            pickupDate: job.pickup_date
          });
          
          // Lock delivery type to the job's delivery type
          setSelectedDeliveryType(job.delivery_type || 'houses');
          
          // Houses already delivered on this job (other workers, previous
          // sessions, admin marks) — shown as done + not re-creditable,
          // but kept OUT of this worker's own delivered set.
          if (job.delivered_house_ids && job.delivered_house_ids.length > 0) {
            setPreDeliveredHouses(new Set(job.delivered_house_ids));
          }
          
          // Fetch server-side tracking progress to restore previous session data
          try {
            const progressResponse = await axios.get(
              `${BACKEND_URL}/api/tracking/progress/${job.id}`,
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            
            if (progressResponse.data) {
              const serverProgress = progressResponse.data;
              console.log('Loaded server-side progress:', serverProgress);
              
              // Merge server progress with any local progress
              const localState = restoreTrackingState();
              const useServerData = !localState || 
                (localState.jobId !== job.id) || 
                (serverProgress.deliveries_count > (localState?.deliveries || 0));
              
              if (useServerData && serverProgress.deliveries_count > 0) {
                // Server has more recent/complete data - use it
                setDeliveries(serverProgress.deliveries_count || 0);
                setDuration(serverProgress.duration || 0);
                setDistance(serverProgress.distance || 0);
                
                // Restore route path from server
                if (serverProgress.route_path && serverProgress.route_path.length > 0) {
                  setRoutePath(serverProgress.route_path);
                }
                
                // Restore THIS worker's own delivered houses from their
                // user-scoped server progress. The job-wide set stays in
                // preDeliveredHouses (display-only) — merging it here made
                // every worker resubmit other workers' houses as their own.
                setDeliveredHouses(new Set(serverProgress.delivered_house_ids || []));
                
                // Calculate earnings
                const payPerFlyer = job.pay_per_flyer || 0.50;
                setEarnings((serverProgress.deliveries_count || 0) * payPerFlyer);
                
                toast.info('Previous progress restored from server', {
                  description: `${serverProgress.deliveries_count} deliveries, ${(serverProgress.delivered_house_ids || []).length} houses marked`,
                  duration: 4000
                });
              }
            }
          } catch (progressError) {
            console.log('No server-side progress found or error fetching:', progressError.message);
          }
        } else {
          // No active job - user needs to accept a job first
          setCurrentJob(null);
        }
      } catch (err) {
        console.error('Error fetching active job:', err);
        setCurrentJob(null);
      } finally {
        setLoadingJob(false);
      }
    };

    fetchJob();
  }, [restoreTrackingState]);
  
  const [mapCenter, setMapCenter] = useState([40.7128, -74.0060]);
  const [mapBounds, setMapBounds] = useState(null);
  
  // Update map center when job loads
  useEffect(() => {
    if (currentJob?.mapCenter) {
      setMapCenter(currentJob.mapCenter);
    }
  }, [currentJob]);
  
  // Restore tracking state when component mounts and job is loaded
  useEffect(() => {
    if (!currentJob || loadingJob) return;
    
    const savedState = restoreTrackingState();
    if (savedState && savedState.jobId === currentJob.id) {
      // Check if the saved state is less than 12 hours old
      const savedAt = new Date(savedState.savedAt);
      const now = new Date();
      const hoursOld = (now - savedAt) / (1000 * 60 * 60);
      
      if (hoursOld < 12) {
        // Only show resume modal if there's meaningful progress
        if (savedState.deliveries > 0 || savedState.duration > 60 || (savedState.routePath && savedState.routePath.length > 5)) {
          setSavedSessionData(savedState);
          setShowResumeSessionModal(true);
        }
      } else {
        // Clear stale state
        clearTrackingState();
      }
    }
  }, [currentJob, loadingJob, restoreTrackingState, clearTrackingState]);
  
  // Handle resuming a saved session
  const handleResumeSession = useCallback(() => {
    if (savedSessionData) {
      setDeliveries(savedSessionData.deliveries || 0);
      setDuration(savedSessionData.duration || 0);
      setDistance(savedSessionData.distance || 0);
      setEarnings(savedSessionData.earnings || 0);
      setRoutePath(savedSessionData.routePath || []);
      setSelectedDeliveryType(savedSessionData.selectedDeliveryType || 'houses');
      
      if (savedSessionData.deliveredHouses && Array.isArray(savedSessionData.deliveredHouses)) {
        setDeliveredHouses(new Set(savedSessionData.deliveredHouses));
      }
      
      if (savedSessionData.manualDeliveryLocations) {
        setManualDeliveryLocations(savedSessionData.manualDeliveryLocations);
      }
      
      toast.success('Session restored!', {
        description: `Resuming with ${savedSessionData.deliveries} deliveries`,
        duration: 3000
      });
    }
    setShowResumeSessionModal(false);
  }, [savedSessionData]);
  
  // Handle discarding a saved session
  const handleDiscardSession = useCallback(() => {
    clearTrackingState();
    setSavedSessionData(null);
    setShowResumeSessionModal(false);
    toast.info('Starting fresh', { duration: 2000 });
  }, [clearTrackingState]);
  
  const payPerFlyer = currentJob?.payPerFlyer || 0.50;
  const hourlyRate = 15.00; // Hourly rate for Cars and Hands delivery types

  // Check if delivery type uses GPS front door detection (Houses and Apartments only)
  const usesFrontDoorDetection = selectedDeliveryType === 'houses' || selectedDeliveryType === 'apartments';
  // Check if delivery type is paid hourly (Cars and Hands)
  const isPaidHourly = selectedDeliveryType === 'cars' || selectedDeliveryType === 'hands';

  const deliveryTypes = [
    { id: 'houses', nameKey: 'track_houses', icon: Home, payType: 'per_flyer' },
    { id: 'apartments', nameKey: 'track_apartments', icon: Building2, payType: 'per_flyer' },
    { id: 'cars', nameKey: 'track_cars', icon: Car, payType: 'hourly' },
    { id: 'hands', nameKey: 'track_hands', icon: Hand, payType: 'hourly' },
  ];

  // Calculate bounds from delivery boundary
  useEffect(() => {
    if (currentJob?.deliveryBoundary) {
      const bounds = L.latLngBounds(currentJob.deliveryBoundary);
      setMapBounds(bounds);
    }
  }, [currentJob]);

  // Calculate distance between two GPS points in meters (Haversine formula)
  const calculateDistanceMeters = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  // Check if a point is inside a polygon boundary (Ray casting algorithm)
  const isPointInsidePolygon = useCallback((lat, lng, boundary) => {
    if (!boundary || boundary.length < 3) return true; // No boundary = always inside
    
    let inside = false;
    for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
      const xi = boundary[i][0], yi = boundary[i][1];
      const xj = boundary[j][0], yj = boundary[j][1];
      if ((yi > lng) !== (yj > lng) && lat < (xj - xi) * (lng - yi) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  // Check if user is in the delivery zone (with buffer for GPS inaccuracy)
  const checkIfInDeliveryZone = useCallback((lat, lng) => {
    if (!currentJob?.deliveryBoundary || currentJob.deliveryBoundary.length < 3) {
      return true; // No boundary defined = always in zone
    }
    
    // First check if inside the polygon
    if (isPointInsidePolygon(lat, lng, currentJob.deliveryBoundary)) {
      return true;
    }
    
    // If outside, check if within 50m buffer (GPS inaccuracy tolerance)
    const BUFFER_METERS = 50;
    for (const point of currentJob.deliveryBoundary) {
      const distToEdge = calculateDistanceMeters(lat, lng, point[0], point[1]);
      if (distToEdge < BUFFER_METERS) {
        return true; // Close enough to boundary
      }
    }
    
    return false;
  }, [currentJob?.deliveryBoundary, isPointInsidePolygon, calculateDistanceMeters]);

  // Generate grid points within polygon boundary to ensure minimum house count
  const generateGridPoints = useCallback((boundary, existingCount, targetCount = 525) => {
    if (existingCount >= targetCount || boundary.length < 3) return [];
    
    const neededPoints = targetCount - existingCount;
    const generatedPoints = [];
    
    // Calculate bounding box
    const lats = boundary.map(p => p[0]);
    const lngs = boundary.map(p => p[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Calculate grid size to generate enough points
    const gridSize = Math.ceil(Math.sqrt(neededPoints * 1.5));
    const latStep = (maxLat - minLat) / gridSize;
    const lngStep = (maxLng - minLng) / gridSize;
    
    // Point-in-polygon check
    const isInsidePolygon = (lat, lng) => {
      let inside = false;
      for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
        const xi = boundary[i][0], yi = boundary[i][1];
        const xj = boundary[j][0], yj = boundary[j][1];
        if ((yi > lng) !== (yj > lng) && lat < (xj - xi) * (lng - yi) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      return inside;
    };
    
    // Generate grid points with slight randomization
    let idCounter = 100000;
    for (let i = 0; i <= gridSize && generatedPoints.length < neededPoints; i++) {
      for (let j = 0; j <= gridSize && generatedPoints.length < neededPoints; j++) {
        const lat = minLat + (i * latStep) + (Math.random() * latStep * 0.5);
        const lng = minLng + (j * lngStep) + (Math.random() * lngStep * 0.5);
        
        if (isInsidePolygon(lat, lng)) {
          generatedPoints.push({
            id: `gen_${idCounter++}`,
            lat,
            lon: lng,
            address: '',
            street: '',
            isGenerated: true
          });
        }
      }
    }
    
    return generatedPoints.slice(0, neededPoints);
  }, []);

  // Fetch houses in delivery zone (for Houses delivery type)
  const fetchHouseLocations = useCallback(async () => {
    if (!currentJob?.deliveryBoundary || currentJob.deliveryBoundary.length < 3) return;
    if (housesFetchAttempted) return; // Don't re-fetch if already tried
    
    setLoadingHouses(true);
    setHousesFetchAttempted(true); // Mark as attempted to prevent re-fetching
    
    try {
      // Use the new backend endpoint which handles OSM + Google fallback
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${currentJob.id}/find-houses`,
        {
          boundary: currentJob.deliveryBoundary,
          use_google_fallback: true
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000 // 90 seconds for fallback
        }
      );
      
      const { houses, count, source, google_used, google_blocked } = response.data;
      
      if (count === 0) {
        let message = 'No houses found in this zone. Use Manual Tracking to mark deliveries.';
        if (google_blocked) {
          message = 'No houses found. Google Maps backup unavailable (monthly limit reached). Use Manual Tracking.';
        }
        toast.info(message, { duration: 5000 });
        setManualTrackingMode(true);
      } else {
        setHouseLocations(houses);
        cacheJobHouses(currentJob?.id, houses); // offline street-marking check
        let successMsg = t('toast_houses_found')?.replace('{count}', count) || `Found ${count} houses`;
        if (google_used) {
          successMsg += ' (via Google Maps backup)';
        }
        toast.success(successMsg);
        
        // Log if Google was used
        if (google_used) {
          console.log('Google Maps API was used as fallback for house detection');
        }
      }
      
    } catch (error) {
      console.error('Error fetching house locations:', error);
      
      // Fallback to direct Overpass API call if backend fails
      try {
        const polygonCoords = currentJob.deliveryBoundary
          .map(p => `${p[0]} ${p[1]}`)
          .join(' ');
        const closedPolygon = `${polygonCoords} ${currentJob.deliveryBoundary[0][0]} ${currentJob.deliveryBoundary[0][1]}`;
        
        const query = `
          [out:json][timeout:60];
          (
            way["building"="house"](poly:"${closedPolygon}");
            way["building"="detached"](poly:"${closedPolygon}");
            way["building"="semidetached_house"](poly:"${closedPolygon}");
            way["building"="terrace"](poly:"${closedPolygon}");
            way["building"="bungalow"](poly:"${closedPolygon}");
            way["building"="residential"]["addr:housenumber"](poly:"${closedPolygon}");
          );
          out body center;
        `;
        
        const osmResponse = await axios.post(
          'https://overpass-api.de/api/interpreter',
          `data=${encodeURIComponent(query)}`,
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            timeout: 60000
          }
        );
        
        const excludeBuildingTypes = [
          'commercial', 'industrial', 'retail', 'warehouse', 'office', 
          'shop', 'supermarket', 'store', 'mall', 'kiosk',
          'garage', 'garages', 'carport', 'parking',
          'shed', 'barn', 'farm', 'farm_auxiliary',
          'church', 'chapel', 'mosque', 'temple', 'synagogue', 'religious',
          'school', 'university', 'college', 'kindergarten',
          'hospital', 'clinic', 'doctors',
          'stadium', 'sports_centre', 'sports_hall', 'pavilion',
          'bridge', 'roof', 'ruins', 'construction', 'greenhouse', 
          'hut', 'cabin', 'service', 'transformer_tower', 'water_tower',
          'apartments', 'apartment', 'flats', 'dormitory'
        ];
        
        const commercialTags = ['shop', 'amenity', 'office', 'tourism', 'leisure'];
        
        const houses = osmResponse.data.elements
          .filter(el => {
            const lat = el.lat || el.center?.lat;
            const lon = el.lon || el.center?.lon;
            if (!lat || !lon) return false;
            
            const tags = el.tags || {};
            const buildingType = tags.building || '';
            
            if (excludeBuildingTypes.includes(buildingType)) return false;
            for (const tag of commercialTags) {
              if (tags[tag]) return false;
            }
            if (tags.brand || tags.operator) return false;
            
            return true;
          })
          .map(el => ({
            id: el.id,
            lat: el.lat || el.center?.lat,
            lon: el.lon || el.center?.lon,
            address: el.tags?.['addr:housenumber'] || '',
            street: el.tags?.['addr:street'] || '',
            source: 'osm'
          }));
        
        // Remove duplicates
        const uniqueHouses = [];
        for (const house of houses) {
          const isDuplicate = uniqueHouses.some(h => 
            Math.abs(h.lat - house.lat) < 0.0001 && Math.abs(h.lon - house.lon) < 0.0001
          );
          if (!isDuplicate) uniqueHouses.push(house);
        }
        
        if (uniqueHouses.length === 0) {
          toast.info('No houses found in this zone. Use Manual Tracking to mark deliveries.', {
            duration: 5000
          });
          setManualTrackingMode(true);
        } else {
          setHouseLocations(uniqueHouses);
          cacheJobHouses(currentJob?.id, uniqueHouses); // offline street-marking check
          toast.success(t('toast_houses_found')?.replace('{count}', uniqueHouses.length) || `Found ${uniqueHouses.length} houses`);
        }
        
      } catch (fallbackError) {
        console.error('Fallback house fetch also failed:', fallbackError);
        // Offline / server unreachable: fall back to the cached house map so
        // deliveries and the street-marking distance check keep working.
        const cachedHouses = getCachedJobHouses(currentJob?.id);
        if (cachedHouses.length > 0) {
          setHouseLocations(cachedHouses);
          toast.success(`Using ${cachedHouses.length} saved houses (offline mode)`, { duration: 4000 });
        } else {
          toast.info('House data unavailable. Use Manual Tracking to mark deliveries.', {
            duration: 5000
          });
          setManualTrackingMode(true);
        }
      }
    } finally {
      setLoadingHouses(false);
    }
  }, [currentJob?.deliveryBoundary, currentJob?.id, t, housesFetchAttempted]);

  // Fetch houses when job loads (for Houses and Apartments delivery types)
  useEffect(() => {
    // Only fetch if we have a boundary, need front door detection, and haven't tried yet
    if (currentJob?.deliveryBoundary && usesFrontDoorDetection && !housesFetchAttempted && !loadingHouses) {
      fetchHouseLocations();
    }
  }, [currentJob?.deliveryBoundary, usesFrontDoorDetection, housesFetchAttempted, loadingHouses, fetchHouseLocations]);

  // Reset fetch attempt flag when job changes
  useEffect(() => {
    if (currentJob?.id) {
      setHousesFetchAttempted(false);
      setHouseLocations([]);
    }
  }, [currentJob?.id]);

  // Play delivery sound
  const playDeliverySound = useCallback(() => {
    if (audioEnabled) {
      try {
        // Use Web Audio API for a simple beep
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      } catch (e) {
        console.log('Audio not available');
      }
    }
  }, [audioEnabled]);

  // Manual delivery credit (for manual tracking mode)
  const handleManualDelivery = useCallback(() => {
    if (!isTracking || isPaused) return;

    // Anti-cheat: how far is the worker from the NEAREST mapped house?
    const FAR_FROM_DOOR_METERS = 30; // ~100 ft
    let nearestHouseM = null;
    if (currentPosition && houseLocations.length > 0) {
      let best = Infinity;
      for (const h of houseLocations) {
        const d = calculateDistanceMeters(currentPosition[0], currentPosition[1], h.lat, h.lon);
        if (d < best) best = d;
      }
      nearestHouseM = Math.round(best);
    }
    const isFlagged = nearestHouseM !== null && nearestHouseM > FAR_FROM_DOOR_METERS;

    // Record the current GPS position for this delivery
    const deliveryLocation = currentPosition ? {
      id: `manual-${Date.now()}`,
      position: [...currentPosition], // Clone the position array
      timestamp: new Date().toISOString(),
      lat: currentPosition[0],
      lng: currentPosition[1],
      ...(isFlagged ? { flagged: true, nearest_house_m: nearestHouseM } : {})
    } : null;
    
    if (deliveryLocation) {
      setManualDeliveryLocations(prev => [...prev, deliveryLocation]);
      console.log('Manual delivery marked at:', deliveryLocation);
    }
    
    setDeliveries(prev => {
      const newDeliveries = prev + 1;
      // Save state immediately after delivery is made
      if (currentJob?.id) {
        setTimeout(() => {
          saveTrackingState({
            jobId: currentJob.id,
            deliveries: newDeliveries,
            deliveredHouses: Array.from(deliveredHouses),
            distance,
            duration,
            routePath,
            selectedDeliveryType,
            earnings,
            manualDeliveryLocations: deliveryLocation 
              ? [...manualDeliveryLocations, deliveryLocation] 
              : manualDeliveryLocations
          });
        }, 100);
      }
      return newDeliveries;
    });
    playDeliverySound();
    
    // Add vibration feedback for web/mobile - noticeable pattern
    if (navigator.vibrate) {
      // Vibrate pattern: 50ms vibrate, 30ms pause, 50ms vibrate (double tap feel)
      navigator.vibrate([50, 30, 50]);
    }
    
    toast.success('Delivery marked!', {
      description: currentPosition 
        ? `Location: ${currentPosition[0].toFixed(5)}, ${currentPosition[1].toFixed(5)}`
        : `${deliveries + 1} deliveries completed`,
      duration: 2000,
    });

    // Anti-cheat: far-from-door popup warning + server-side flag
    if (isFlagged) {
      flaggedMarksRef.current += 1;
      setPolicyWarning({ distanceFt: Math.round(nearestHouseM * 3.281), count: flaggedMarksRef.current });
      if (navigator.vibrate) {
        navigator.vibrate([200, 80, 200, 80, 200]);
      }
      jobsAPI.reportDeliveryViolation({
        job_id: currentJob?.id,
        lat: currentPosition[0],
        lng: currentPosition[1],
        nearest_house_m: nearestHouseM
      }).catch(() => {});
    }
  }, [isTracking, isPaused, deliveries, playDeliverySound, currentPosition, currentJob?.id, deliveredHouses, distance, duration, routePath, selectedDeliveryType, earnings, manualDeliveryLocations, saveTrackingState, houseLocations, calculateDistanceMeters]);

  // Auto-credit delivery for a house
  const creditDelivery = useCallback((house) => {
    if (deliveredHouses.has(house.id) || preDeliveredHouses.has(house.id)) return;
    
    const newDeliveredHouses = new Set([...deliveredHouses, house.id]);
    setDeliveredHouses(newDeliveredHouses);
    setDeliveries(prev => {
      const newDeliveries = prev + 1;
      // Save state immediately after delivery is made
      if (currentJob?.id) {
        setTimeout(() => {
          saveTrackingState({
            jobId: currentJob.id,
            deliveries: newDeliveries,
            deliveredHouses: Array.from(newDeliveredHouses),
            distance,
            duration,
            routePath,
            selectedDeliveryType,
            earnings,
            manualDeliveryLocations
          });
        }, 100);
      }
      return newDeliveries;
    });
    setNearbyHouse(null);
    setStopTimer(0);
    
    playDeliverySound();
    
    const addressText = house.address ? `#${house.address}` : t('track_houses');
    const streetText = house.street ? ` ${house.street}` : '';
    toast.success(`${addressText}${streetText} ${t('toast_delivery_credited')}`, {
      description: t('toast_gps_confirmed'),
      duration: 2500,
    });
  }, [deliveredHouses, preDeliveredHouses, playDeliverySound, t, currentJob?.id, distance, duration, routePath, selectedDeliveryType, earnings, manualDeliveryLocations, saveTrackingState]);

  // Check if near any undelivered house
  const checkNearbyHouse = useCallback((lat, lon) => {
    if (houseLocations.length === 0) return null;
    
    for (const house of houseLocations) {
      if (deliveredHouses.has(house.id) || preDeliveredHouses.has(house.id)) continue;
      
      const dist = calculateDistanceMeters(lat, lon, house.lat, house.lon);
      if (dist <= HOUSE_PROXIMITY_METERS) {
        return house;
      }
    }
    return null;
  }, [houseLocations, deliveredHouses, preDeliveredHouses, calculateDistanceMeters]);

  // Check if user has stopped (not moving)
  const checkIfStopped = useCallback((lat, lon) => {
    if (!lastMovementPositionRef.current) {
      lastMovementPositionRef.current = { lat, lon };
      return true;
    }
    
    const dist = calculateDistanceMeters(
      lastMovementPositionRef.current.lat,
      lastMovementPositionRef.current.lon,
      lat, lon
    );
    
    if (dist > MOVEMENT_THRESHOLD_METERS) {
      // User moved
      lastMovementPositionRef.current = { lat, lon };
      return false;
    }
    
    return true; // User is stopped
  }, [calculateDistanceMeters]);

  // Convert meters to miles
  const metersToMiles = (meters) => meters * 0.000621371;

  // Process new GPS position
  const processPosition = useCallback((position) => {
    const { latitude, longitude, accuracy } = position.coords;
    const newPos = [latitude, longitude];
    const timestamp = new Date();
    
    setCurrentPosition(newPos);
    setGpsAccuracy(Math.round(accuracy));
    setLastUpdateTime(timestamp);
    setGpsError(null);
    
    // Determine signal strength based on accuracy
    if (accuracy <= 5) {
      setGpsSignalStrength('excellent');
    } else if (accuracy <= 10) {
      setGpsSignalStrength('good');
    } else if (accuracy <= 20) {
      setGpsSignalStrength('fair');
    } else {
      setGpsSignalStrength('poor');
    }

    // Only add to route if tracking and not paused
    if (isTracking && !isPaused) {
      const lastPos = lastPositionRef.current;
      
      // Check if user is outside the delivery zone
      const inZone = checkIfInDeliveryZone(latitude, longitude);
      if (!inZone) {
        outOfZoneCountRef.current += 1;
        // Only show warning after 3 consecutive out-of-zone readings (to avoid false positives)
        // and don't spam warnings (wait at least 30 seconds between warnings)
        const now = Date.now();
        const timeSinceLastWarning = lastZoneWarningRef.current ? now - lastZoneWarningRef.current : Infinity;
        
        if (outOfZoneCountRef.current >= 3 && timeSinceLastWarning > 30000) {
          setIsOutsideZone(true);
          setShowOutOfZoneWarning(true);
          lastZoneWarningRef.current = now;
        }
      } else {
        outOfZoneCountRef.current = 0;
        setIsOutsideZone(false);
      }
      
      if (lastPos) {
        // Calculate distance from last recorded point
        const distFromLast = calculateDistanceMeters(
          lastPos[0], lastPos[1], 
          latitude, longitude
        );
        
        // Only record if moved more than threshold (reduces GPS noise)
        if (distFromLast >= MIN_DISTANCE_THRESHOLD) {
          setDistance(d => d + metersToMiles(distFromLast));
          setRoutePath(prev => [...prev, newPos]);
          lastPositionRef.current = newPos;
          
          // Save GPS point for offline backup (regardless of connection status)
          if (currentJob?.id) {
            saveGPSPoint({ ...newPos, accuracy }, currentJob.id);
          }
        }
      } else {
        // First point
        setRoutePath(prev => [...prev, newPos]);
        lastPositionRef.current = newPos;
        
        // Save first GPS point for offline backup
        if (currentJob?.id) {
          saveGPSPoint({ ...newPos, accuracy }, currentJob.id);
        }
      }
      
      // Auto-delivery detection (only for Houses and Apartments)
      if (usesFrontDoorDetection && houseLocations.length > 0) {
        const nearby = checkNearbyHouse(latitude, longitude);
        const isStopped = checkIfStopped(latitude, longitude);
        
        if (nearby && isStopped) {
          setNearbyHouse(nearby);
        } else if (!nearby || !isStopped) {
          setNearbyHouse(null);
          setStopTimer(0);
        }
      }
    }
  }, [isTracking, isPaused, calculateDistanceMeters, usesFrontDoorDetection, houseLocations, checkNearbyHouse, checkIfStopped, currentJob?.id, saveGPSPoint, checkIfInDeliveryZone]);

  // Handle GPS error
  const handleGpsError = useCallback((error) => {
    console.error('GPS Error:', error);
    setGpsSignalStrength('error');
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        setGpsError(t('track_no_gps') + '. ' + 'Please enable location access.');
        break;
      case error.POSITION_UNAVAILABLE:
        setGpsError(t('track_no_gps') + '. ' + 'Make sure GPS is enabled.');
        break;
      case error.TIMEOUT:
        setGpsError('Location request timed out. Retrying...');
        break;
      default:
        setGpsError('Unable to get location. Please try again.');
    }
  }, [t]);

  // Get initial position on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentPosition([latitude, longitude]);
          setGpsSignalStrength('ready');
        },
        handleGpsError,
        GPS_CONFIG
      );
    } else {
      setGpsError('Geolocation is not supported by this browser.');
    }
  }, [handleGpsError]);

  // Stop timer for auto-delivery (6-second countdown when near a house)
  useEffect(() => {
    if (nearbyHouse && isTracking && !isPaused) {
      // Start counting seconds stopped
      stopTimerRef.current = setInterval(() => {
        setStopTimer(prev => {
          const newValue = prev + 1;
          if (newValue >= STOP_DURATION_SECONDS) {
            // Trigger auto-delivery
            creditDelivery(nearbyHouse);
            clearInterval(stopTimerRef.current);
            return 0;
          }
          return newValue;
        });
      }, 1000);
      
      return () => {
        if (stopTimerRef.current) {
          clearInterval(stopTimerRef.current);
        }
      };
    } else {
      // Clear timer if moved away
      if (stopTimerRef.current) {
        clearInterval(stopTimerRef.current);
      }
      setStopTimer(0);
    }
  }, [nearbyHouse, isTracking, isPaused, creditDelivery]);

  // Process position from background location service
  const handleBackgroundPosition = useCallback((position) => {
    // Convert background location format to geolocation format
    const geoPosition = {
      coords: {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        altitude: position.altitude,
        speed: position.speed,
        heading: position.heading,
      },
      timestamp: position.timestamp || Date.now(),
    };
    processPosition(geoPosition);
  }, [processPosition]);

  // Handle background location errors
  const handleBackgroundError = useCallback((error) => {
    console.error('[TrackPage] Background location error:', error);
    setGpsError(error.message || 'Location error');
  }, []);

  // Start GPS tracking - uses background service on native, web geolocation on browser
  const startGpsTracking = useCallback(async () => {
    // Try background tracking first if supported (native app)
    if (backgroundLocationService.isBackgroundSupported()) {
      console.log('[TrackPage] Starting background location tracking...');
      const started = await backgroundLocationService.startTracking(
        handleBackgroundPosition,
        handleBackgroundError
      );
      
      if (started) {
        setUsingBackgroundTracking(true);
        toast.success('Background tracking enabled - tracking continues even when app is minimized');
        return;
      } else {
        console.warn('[TrackPage] Background tracking failed, falling back to web tracking');
      }
    }

    // Fallback to web geolocation
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported');
      return;
    }

    setUsingBackgroundTracking(false);
    console.log('[TrackPage] Using web geolocation (foreground only)');

    // Clear any existing tracking
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    // Start continuous watching with high accuracy
    watchIdRef.current = navigator.geolocation.watchPosition(
      processPosition,
      handleGpsError,
      GPS_CONFIG
    );

    // Backup ping every 5 seconds to ensure we get updates
    // This catches cases where watchPosition might be slow
    pingIntervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        processPosition,
        (error) => {
          console.log('Ping error (non-critical):', error);
        },
        GPS_CONFIG
      );
    }, PING_INTERVAL);

  }, [processPosition, handleGpsError, handleBackgroundPosition, handleBackgroundError]);

  // Stop GPS tracking
  const stopGpsTracking = useCallback(async () => {
    // Stop background tracking if active
    if (usingBackgroundTracking) {
      await backgroundLocationService.stopTracking();
      setUsingBackgroundTracking(false);
    }
    
    // Stop web tracking
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (stopTimerRef.current) {
      clearInterval(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }, [usingBackgroundTracking]);

  // Cleanup background tracking on unmount
  useEffect(() => {
    return () => {
      // Stop background tracking when component unmounts
      if (backgroundLocationService.isBackgroundSupported()) {
        backgroundLocationService.stopTracking().catch(() => {});
      }
    };
  }, []);

  // Send live location updates to server
  const sendLiveLocationUpdate = useCallback(async () => {
    if (!currentJob?.id || currentJob.id === 'demo' || !currentPosition || !online) {
      return;
    }
    
    try {
      await jobsAPI.updateLiveLocation(currentJob.id, {
        job_id: currentJob.id,
        latitude: currentPosition[0],
        longitude: currentPosition[1],
        accuracy: gpsAccuracy,
        route_path: routePath
      });
    } catch (err) {
      // Log the error for debugging - GPS updates are critical for customer tracking
      console.warn('Live location update failed:', err.response?.data?.detail || err.message);
      // If it's an auth error (job not assigned), show a warning once
      if (err.response?.status === 404) {
        console.error('Job assignment issue - GPS updates not being saved. Job ID:', currentJob.id);
      }
    }
  }, [currentJob?.id, currentPosition, gpsAccuracy, routePath, online]);

  // Save tracking progress to server (for auto-save and manual "Update my data" button)
  const saveProgressToServer = useCallback(async (showToast = false) => {
    if (!currentJob?.id || currentJob.id === 'demo' || !online) {
      if (showToast) {
        toast.error('Cannot save - you are offline or no active job');
      }
      return false;
    }
    
    try {
      setServerSaving(true);
      const response = await jobsAPI.saveTrackingProgress(currentJob.id, {
        deliveries_count: deliveries,
        duration: duration,
        distance: distance,
        route_path: routePath,
        delivered_house_ids: Array.from(deliveredHouses),
        manual_delivery_locations: manualDeliveryLocations
      });
      
      setLastServerSave(new Date());
      
      if (showToast) {
        toast.success('Data saved to server!', {
          description: `${response.data.deliveries_saved} deliveries, ${response.data.route_points_saved} GPS points`,
          duration: 2000
        });
      }
      
      return true;
    } catch (err) {
      console.error('Failed to save progress to server:', err);
      if (showToast) {
        toast.error('Failed to save data', {
          description: err.response?.data?.detail || 'Please try again'
        });
      }
      return false;
    } finally {
      setServerSaving(false);
    }
  }, [currentJob?.id, deliveries, duration, distance, routePath, deliveredHouses, manualDeliveryLocations, online]);

  // Latest-value refs so the tracking intervals below never need to be
  // torn down when state changes (the old deps-based effect re-ran every
  // second via `duration`, so the 15s/10s/30s intervals never fired).
  const trackingFnsRef = useRef({});
  useEffect(() => {
    trackingFnsRef.current = {
      startGps: startGpsTracking,
      stopGps: stopGpsTracking,
      sendLiveLocation: sendLiveLocationUpdate,
      saveProgress: saveProgressToServer,
      jobId: currentJob?.id,
      online,
      saveSnapshot: () => {
        if (currentJob?.id) {
          saveTrackingState({
            jobId: currentJob.id,
            deliveries,
            deliveredHouses: Array.from(deliveredHouses),
            distance,
            duration,
            routePath,
            selectedDeliveryType,
            earnings,
            manualDeliveryLocations
          });
        }
      },
    };
  });

  // Handle tracking state changes
  useEffect(() => {
    const fns = () => trackingFnsRef.current;
    if (isTracking && !isPaused) {
      fns().startGps();
      
      // Start duration timer
      timerIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
      // Periodically save tracking state for offline recovery (every 30 seconds)
      offlineSaveIntervalRef.current = setInterval(() => {
        fns().saveSnapshot();
      }, 30000);
      
      // Send live location updates to server every 10 seconds (for customer tracking)
      liveLocationIntervalRef.current = setInterval(() => {
        fns().sendLiveLocation();
      }, 10000);
      
      // Server-side auto-save every 15 seconds (prevents data loss if app crashes)
      serverSaveIntervalRef.current = setInterval(() => {
        fns().saveProgress(false); // Silent save
      }, 15000);
      
      // Send initial location immediately
      fns().sendLiveLocation();
    } else {
      if (isPaused) {
        // Just pause the timer, keep GPS watching
        clearInterval(timerIntervalRef.current);
        clearInterval(offlineSaveIntervalRef.current);
        clearInterval(liveLocationIntervalRef.current);
        clearInterval(serverSaveIntervalRef.current);
        
        // Save state immediately when pausing (both local and server)
        fns().saveSnapshot();
        fns().saveProgress(false);
      } else {
        // Fully stopped
        fns().stopGps();
        clearInterval(timerIntervalRef.current);
        clearInterval(offlineSaveIntervalRef.current);
        clearInterval(liveLocationIntervalRef.current);
        clearInterval(serverSaveIntervalRef.current);
        
        // Clear live location from server when tracking stops
        const { jobId, online: isOnline } = fns();
        if (jobId && jobId !== 'demo' && isOnline) {
          jobsAPI.clearLiveLocation(jobId).catch(() => {});
        }
      }
    }

    return () => {
      clearInterval(timerIntervalRef.current);
      clearInterval(offlineSaveIntervalRef.current);
      clearInterval(liveLocationIntervalRef.current);
      clearInterval(serverSaveIntervalRef.current);
    };
  }, [isTracking, isPaused]);

  // Cleanup on unmount - save state before leaving
  useEffect(() => {
    return () => {
      stopGpsTracking();
      clearInterval(timerIntervalRef.current);
      clearInterval(offlineSaveIntervalRef.current);
      clearInterval(liveLocationIntervalRef.current);
      clearInterval(serverSaveIntervalRef.current);
      if (stopTimerRef.current) {
        clearInterval(stopTimerRef.current);
      }
    };
  }, [stopGpsTracking]);
  
  // Save state before page unload (browser close, refresh, navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isTracking && currentJob?.id) {
        // Sync save before unload
        const state = {
          jobId: currentJob.id,
          deliveries,
          deliveredHouses: Array.from(deliveredHouses),
          distance,
          duration,
          routePath,
          selectedDeliveryType,
          earnings,
          manualDeliveryLocations
        };
        // Use localStorage directly for synchronous save
        try {
          localStorage.setItem('localflyer_offline_data', JSON.stringify({
            ...state,
            savedAt: new Date().toISOString(),
            version: 1
          }));
          
          // Also try to save to server using sendBeacon (non-blocking, survives page close)
          const token = localStorage.getItem('token');
          if (token && navigator.sendBeacon) {
            const progressData = JSON.stringify({
              deliveries_count: deliveries,
              duration: duration,
              distance: distance,
              route_path: routePath,
              delivered_house_ids: Array.from(deliveredHouses),
              manual_delivery_locations: manualDeliveryLocations
            });
            
            navigator.sendBeacon(
              `${BACKEND_URL}/api/tracking/progress/${currentJob.id}/beacon?token=${token}`,
              new Blob([progressData], { type: 'application/json' })
            );
            console.log('Sent beacon save on page unload');
          }
        } catch (e) {
          console.error('Error saving state before unload:', e);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    // Also handle visibility change (user switching tabs/apps)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isTracking && currentJob?.id) {
        handleBeforeUnload();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTracking, currentJob?.id, deliveries, deliveredHouses, distance, duration, routePath, selectedDeliveryType, earnings, manualDeliveryLocations]);

  // Cleanup wake lock on unmount
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // Update earnings based on delivery type
  useEffect(() => {
    if (isPaidHourly) {
      // For Cars and Hands: paid by the hour
      const hours = duration / 3600;
      setEarnings(hours * hourlyRate);
    } else {
      // For Houses and Apartments: paid per delivery
      setEarnings(deliveries * payPerFlyer);
    }
  }, [deliveries, payPerFlyer, duration, isPaidHourly, hourlyRate]);

  // Check if position is inside delivery boundary
  const isInsideBoundary = (pos) => {
    if (!pos || !currentJob?.deliveryBoundary) return true;
    const polygon = L.polygon(currentJob.deliveryBoundary);
    return polygon.getBounds().contains(L.latLng(pos[0], pos[1]));
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsTracking(true);
    setIsPaused(false);
    setRoutePath(currentPosition ? [currentPosition] : []);
    lastPositionRef.current = currentPosition;
    lastMovementPositionRef.current = currentPosition ? { lat: currentPosition[0], lon: currentPosition[1] } : null;
    setDeliveredHouses(new Set()); // Reset delivered houses
    setManualDeliveryLocations([]); // Reset manual delivery locations
    setNearbyHouse(null);
    setStopTimer(0);
    
    // Request wake lock to keep screen on during tracking
    requestWakeLock();
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleStop = () => {
    // Release wake lock when stopping
    releaseWakeLock();
    
    // Check if job is incomplete (less than 100% delivered)
    const totalRequired = currentJob?.deliveries || 100;
    const isIncomplete = deliveries < totalRequired;
    
    if (isIncomplete) {
      setShowIncompleteWarning(true);
    } else {
      setShowCompleteModal(true);
    }
  };

  const handleIncompleteConfirm = () => {
    setShowIncompleteWarning(false);
    setShowPartialDeliveryMsg(true);
  };

  const handlePartialDeliveryAcknowledge = () => {
    setShowPartialDeliveryMsg(false);
    setShowCompleteModal(true);
  };

  const handleConfirmComplete = async () => {
    if (!isAuthenticated) {
      // Not logged in, just reset and navigate
      setIsTracking(false);
      setIsPaused(false);
      setShowCompleteModal(false);
      clearTrackingState();
      toast.success(t('track_complete_session'));
      navigate('/');
      return;
    }

    if (currentJob?.id === 'demo') {
      // Demo mode, don't save to backend
      setIsTracking(false);
      setIsPaused(false);
      setShowCompleteModal(false);
      clearTrackingState();
      toast.success(t('track_complete_session'));
      navigate('/');
      return;
    }

    // Check if this is a partial delivery
    const totalRequired = currentJob?.deliveries || 100;
    const isPartial = deliveries < totalRequired;
    
    // Get the list of delivered house IDs from the deliveredHouses Set
    const deliveredHouseIdsList = Array.from(deliveredHouses);
    
    // Merge any offline GPS points into the route
    const finalRoutePath = mergeOfflineRoute(routePath);
    
    // Session data to save
    const sessionData = {
      job_id: currentJob.id,
      delivery_type: selectedDeliveryType,
      deliveries_count: deliveries,
      distance: distance,
      duration: duration,
      route_path: finalRoutePath,
      is_partial: isPartial,
      delivered_house_ids: deliveredHouseIdsList,
      offlineGPSPoints: offlineGPSBuffer
    };

    // If offline, queue the session for later sync
    if (!online) {
      const queued = queueSession(sessionData);
      if (queued) {
        setIsTracking(false);
        setIsPaused(false);
        setShowCompleteModal(false);
        clearTrackingState();
        toast.success('Session saved offline', {
          description: 'Your delivery data has been saved and will sync when you\'re back online.',
          duration: 5000
        });
        navigate('/history');
      } else {
        toast.error('Failed to save session offline. Please try again.');
      }
      return;
    }

    try {
      setSaving(true);
      
      // Save session to backend
      const response = await sessionsAPI.createSession(sessionData);
      
      // Submit manual delivery locations as map corrections (if any)
      if (manualDeliveryLocations.length > 0 && currentJob?.id) {
        try {
          await mapCorrectionsAPI.submitCorrection({
            job_id: currentJob.id,
            job_title: currentJob.title,
            zone_boundary: currentJob.deliveryBoundary,
            map_center: currentJob.mapCenter,
            missing_house_locations: manualDeliveryLocations.map(loc => ({
              position: loc.position,
              timestamp: loc.timestamp
            })),
            notes: `Submitted via manual tracking mode during delivery session`
          });
          console.log(`Submitted ${manualDeliveryLocations.length} map corrections`);
        } catch (correctionErr) {
          // Non-critical - log but don't fail the session save
          console.warn('Failed to submit map corrections:', correctionErr);
        }
      }
      
      // Store session ID for photo uploads
      setLastSessionId(response.data.id);
      setUploadedPhotos([]); // Reset photos for new session
      
      // Clear tracking state after successful save
      clearTrackingState();
      
      // Show appropriate message based on partial status
      if (isPartial && response.data.new_job_id) {
        toast.info('Partial delivery recorded. A new job has been created for remaining deliveries.');
      } else {
        toast.success(t('track_save_complete') + '!');
      }
      
      setIsTracking(false);
      setIsPaused(false);
      setShowCompleteModal(false);
      setShowSuccessModal(true); // Show success modal with payout option
    } catch (err) {
      console.error('Error saving session:', err);
      
      // If network error, offer to save offline
      if (err.code === 'ERR_NETWORK' || !navigator.onLine) {
        const queued = queueSession(sessionData);
        if (queued) {
          setIsTracking(false);
          setIsPaused(false);
          setShowCompleteModal(false);
          clearTrackingState();
          toast.info('Connection lost - session saved offline', {
            description: 'Your data will sync when connection returns.',
            duration: 5000
          });
          navigate('/history');
        } else {
          toast.error('Failed to save session. Please try again.');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save session');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = REQUIRED_PHOTOS - uploadedPhotos.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    setUploadingPhoto(true);
    
    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('session_id', lastSessionId);
        
        const response = await axios.post(
          `${BACKEND_URL}/api/upload-delivery-photo`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        setUploadedPhotos(prev => [...prev, {
          id: response.data.photo_id,
          url: response.data.url,
          thumbnail: response.data.thumbnail || response.data.url
        }]);
        
      } catch (err) {
        console.error('Error uploading photo:', err);
        toast.error(t('photo_upload_error') || 'Failed to upload photo');
      }
    }
    
    setUploadingPhoto(false);
    
    if (uploadedPhotos.length + filesToUpload.length >= REQUIRED_PHOTOS) {
      toast.success(t('photos_complete') || 'All required photos uploaded!');
    }
    
    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Handle request payout
  const handleRequestPayout = () => {
    if (uploadedPhotos.length < REQUIRED_PHOTOS) {
      toast.error(t('photos_required') || `Please upload ${REQUIRED_PHOTOS} photos of flyers on doors before requesting payout`);
      return;
    }
    if (!user?.paypal_email) {
      toast.error(t('payout_add_paypal_first') || 'Please add your PayPal email in your profile first');
      navigate('/profile');
      return;
    }
    toast.success(t('payout_requested') || 'Payout request submitted! You will be paid once an admin approves your delivery.');
    setShowSuccessModal(false);
    navigate('/history');
  };

  const insideBoundary = isInsideBoundary(currentPosition);

  // Get signal strength color and icon
  const getSignalInfo = () => {
    switch (gpsSignalStrength) {
      case 'excellent':
        return { color: 'bg-green-500', text: t('track_excellent_gps'), bars: 4 };
      case 'good':
        return { color: 'bg-green-400', text: t('track_good_gps'), bars: 3 };
      case 'fair':
        return { color: 'bg-yellow-500', text: t('track_fair_gps'), bars: 2 };
      case 'poor':
        return { color: 'bg-orange-500', text: t('track_poor_gps'), bars: 1 };
      case 'error':
        return { color: 'bg-red-500', text: t('track_no_gps'), bars: 0 };
      case 'ready':
        return { color: 'bg-blue-500', text: t('done'), bars: 3 };
      default:
        return { color: 'bg-gray-500', text: t('loading'), bars: 0 };
    }
  };

  const signalInfo = getSignalInfo();

  // Loading state
  if (loadingJob) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // No active job state
  if (!currentJob) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid="track-page-no-job">
        <div className="flex items-center justify-between px-5 pt-12 pb-4 bg-gray-900 z-20">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center"
            data-testid="back-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-semibold">{t('track_delivery')}</h1>
          <div className="w-10" />
        </div>
        
        <div className="flex-1 flex items-center justify-center px-5">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin size={40} className="text-gray-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">{t('track_no_active_job')}</h2>
            <p className="text-gray-400 mb-6">{t('track_accept_job_first')}</p>
            <button
              onClick={() => navigate('/jobs')}
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-3 rounded-xl font-semibold"
              data-testid="find-jobs-btn"
            >
              {t('jobs_find')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid="track-page">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-12 pb-4 bg-gray-900 z-20">
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center"
          data-testid="back-btn"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-semibold">{t('track_delivery')}</h1>
        <button
          onClick={() => setAudioEnabled(!audioEnabled)}
          className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center"
          data-testid="audio-toggle-btn"
        >
          {audioEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
        </button>
      </div>

      {/* Current Job Info */}
      <div className="px-5 mb-3">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3" data-testid="current-job-info">
          <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
            <MapPin size={20} className="text-orange-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-medium">{currentJob.title}</p>
            <p className="text-xs text-gray-400">{currentJob.client} • {currentJob.deliveries} {t('jobs_flyers')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-green-400">${currentJob.totalPay?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-400">{t('jobs_total_pay')}</p>
          </div>
        </div>
      </div>

      {/* GPS Accuracy Indicator */}
      <div className="px-5 mb-3">
        <div className="bg-white/5 rounded-xl p-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Signal size={16} className={signalInfo.color.replace('bg-', 'text-')} />
            <span className="text-xs text-gray-300">{signalInfo.text}</span>
            {gpsAccuracy && (
              <span className="text-xs text-gray-500">±{gpsAccuracy}m</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map((bar) => (
              <div
                key={bar}
                className={`w-1 rounded-full transition-all ${
                  bar <= signalInfo.bars
                    ? signalInfo.color
                    : 'bg-gray-600'
                }`}
                style={{ height: `${bar * 4 + 4}px` }}
              />
            ))}
          </div>
          {lastUpdateTime && isTracking && (
            <span className="text-xs text-gray-500">
              {Math.round((Date.now() - lastUpdateTime.getTime()) / 1000)}s ago
            </span>
          )}
        </div>
      </div>

      {/* Offline Mode Banner */}
      {!online && (
        <div className="px-5 mb-3" data-testid="offline-banner">
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/30 rounded-full flex items-center justify-center flex-shrink-0">
              <WifiOff size={20} className="text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-400">Offline Mode Active</p>
              <p className="text-xs text-gray-400">
                GPS tracking continues. Data will sync when you&apos;re back online.
              </p>
            </div>
            <CloudOff size={16} className="text-yellow-500/50" />
          </div>
        </div>
      )}

      {/* Pending Sync Indicator */}
      {online && hasOfflineData && (
        <div className="px-5 mb-3" data-testid="sync-pending-banner">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0">
              {syncing ? (
                <RefreshCw size={20} className="text-blue-400 animate-spin" />
              ) : (
                <CloudOff size={20} className="text-blue-400" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-400">
                {syncing ? 'Syncing offline data...' : 'Offline data pending'}
              </p>
              <p className="text-xs text-gray-400">
                {pendingCount.sessions > 0 && `${pendingCount.sessions} session${pendingCount.sessions > 1 ? 's' : ''} `}
                {pendingCount.gpsPoints > 0 && `${pendingCount.gpsPoints} GPS points`}
              </p>
            </div>
            {!syncing && (
              <button
                onClick={syncPendingData}
                className="px-3 py-1.5 bg-blue-500/30 rounded-lg text-xs font-medium text-blue-400 hover:bg-blue-500/40 transition-colors"
                data-testid="sync-now-btn"
              >
                Sync Now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Background Tracking Active Indicator */}
      {isTracking && wakeLockActive && (
        <div className="px-5 mb-3" data-testid="background-tracking-banner">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-2 flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-xs text-green-400">
              Background tracking active • Screen stays on • {offlineGPSBuffer.length} points buffered
            </p>
          </div>

      {/* Out of Zone Warning Banner */}
      {isTracking && isOutsideZone && (
        <div className="px-5 mb-3" data-testid="out-of-zone-banner">
          <button
            onClick={() => setShowOutOfZoneWarning(true)}
            className="w-full bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-3 flex items-center gap-3 hover:bg-yellow-500/30 transition-colors"
          >
            <div className="w-10 h-10 bg-yellow-500/30 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
              <AlertCircle size={20} className="text-yellow-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-yellow-400">⚠️ Outside Delivery Zone</p>
              <p className="text-xs text-gray-400">
                Tap here to view your assigned area
              </p>
            </div>
            <Navigation size={16} className="text-yellow-500/50" />
          </button>
        </div>
      )}
        </div>
      )}

      {/* Manual Tracking Mode - PROMINENT POSITION - Shows when manual mode is enabled OR no houses available */}
      {usesFrontDoorDetection && (houseLocations.length === 0 || manualTrackingMode) && !loadingHouses && (
        <div className="px-5 mb-3" data-testid="manual-tracking-section">
          <div className="rounded-xl p-4 bg-purple-500/20 border-2 border-purple-500/50">
            {/* Manual Mode Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <p className="text-base font-bold text-white flex items-center gap-2">
                  <Hand size={18} className="text-purple-400" />
                  Manual Tracking Mode {manualTrackingMode && houseLocations.length > 0 && <span className="text-xs bg-purple-500 px-2 py-0.5 rounded-full">Active</span>}
                </p>
                <p className="text-xs text-gray-300 mt-1">
                  {houseLocations.length === 0 
                    ? 'No houses detected - tap button for each delivery'
                    : 'Tap the button below for each delivery you make'}
                </p>
              </div>
            </div>
            
            {/* Far-from-door policy warning popup */}
            {policyWarning && (
              <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-5" data-testid="policy-warning-overlay">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                  <div className="bg-red-600 px-5 py-4 flex items-center gap-3">
                    <AlertTriangle className="w-7 h-7 text-white shrink-0" />
                    <h3 className="text-white font-bold text-lg leading-tight">Delivery Policy Warning</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-gray-900 font-semibold mb-2" data-testid="policy-warning-distance">
                      This delivery was marked ~{policyWarning.distanceFt} ft from the nearest house.
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      Flyers must be delivered to the front door. Marking deliveries from the street or
                      sidewalk is against company policy and may result in non-payment for this map,
                      removal from the job, or account termination.
                    </p>
                    <p className="text-xs text-red-700 font-semibold mb-4">
                      All marks are GPS-logged and reviewed by management.
                      {policyWarning.count >= 2 && ` This is flagged mark #${policyWarning.count} — your account has been flagged for admin review.`}
                    </p>
                    <button
                      onClick={() => setPolicyWarning(null)}
                      className="w-full py-3 rounded-xl bg-red-600 text-white font-bold text-sm active:scale-95 transition-transform"
                      data-testid="policy-warning-acknowledge-btn"
                    >
                      I Understand — Deliver to the Door
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mark Delivery Button - Only functional when tracking */}
            {isTracking ? (
              <button
                onClick={handleManualDelivery}
                disabled={isPaused}
                className={`w-full py-5 rounded-xl font-bold text-xl flex items-center justify-center gap-3 transform transition-all duration-200 ${
                  isPaused 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.95]'
                }`}
                data-testid="mark-delivery-btn"
              >
                <CheckCircle size={28} />
                Mark Delivery ({deliveries})
              </button>
            ) : (
              <div className="w-full py-4 rounded-xl bg-gray-700/50 border border-gray-600 text-center">
                <p className="text-gray-400 text-sm">
                  Press <span className="text-orange-400 font-bold">Start Tracking</span> below, then use this button to mark each delivery
                </p>
              </div>
            )}
            
            {/* Progress bar - only show when tracking */}
            {isTracking && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
                  <span>Progress: {deliveries} / {currentJob?.deliveries || 0}</span>
                  <span className={deliveries >= (currentJob?.deliveries || 0) ? 'text-green-400 font-bold' : ''}>
                    {Math.round((deliveries / (currentJob?.deliveries || 1)) * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((deliveries / (currentJob?.deliveries || 1)) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Markers placed indicator */}
            {manualDeliveryLocations.length > 0 && (
              <div className="mt-2 text-xs text-purple-300 flex items-center gap-1">
                <MapPin size={12} />
                {manualDeliveryLocations.length} location{manualDeliveryLocations.length !== 1 ? 's' : ''} marked on map
              </div>
            )}
            
            {/* Update My Data Button - Only show when tracking in manual mode */}
            {isTracking && (
              <button
                onClick={() => saveProgressToServer(true)}
                disabled={serverSaving || !online}
                className={`mt-4 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  serverSaving 
                    ? 'bg-blue-600/50 text-blue-200 cursor-wait'
                    : !online
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50'
                }`}
                data-testid="update-my-data-btn"
              >
                {serverSaving ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Update My Data
                  </>
                )}
              </button>
            )}
            
            {/* Last server save indicator */}
            {lastServerSave && isTracking && (
              <p className="mt-2 text-xs text-gray-400 text-center">
                Last saved: {lastServerSave.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Map Section - Large */}
      <div className="relative mx-5 mb-4 rounded-2xl overflow-hidden" style={{ height: '380px' }}>
        {gpsError && !currentPosition && (
          <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
            <div className="text-center p-4">
              <Crosshair size={48} className="mx-auto text-orange-500 mb-3 animate-pulse" />
              <p className="text-gray-300 text-sm">{gpsError}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-orange-500 rounded-lg text-sm font-medium"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        
        <TrackPageGoogleMap
          mapCenter={mapCenter ? { lat: mapCenter[0], lng: mapCenter[1] } : { lat: 29.7604, lng: -95.3698 }}
          deliveryBoundary={currentJob?.deliveryBoundary}
          zoneLabel={currentJob?.zoneLabel}
          currentPosition={currentPosition}
          routePath={routePath}
          houseLocations={houseLocations}
          deliveredHouses={preDeliveredHouses.size > 0 ? new Set([...deliveredHouses, ...preDeliveredHouses]) : deliveredHouses}
          manualDeliveryLocations={manualDeliveryLocations}
          nearbyHouse={nearbyHouse}
          isTracking={isTracking}
          proximityRadius={HOUSE_PROXIMITY_METERS}
        />

        {/* Map overlay - Delivery Zone Label with Grid info */}
        <div className="absolute top-3 left-3 bg-orange-500/90 backdrop-blur-sm rounded-lg px-3 py-1.5 z-[1000]">
          <p className="text-xs font-semibold text-white flex items-center gap-1.5">
            <MapPin size={12} />
            {currentJob?.zoneLabel ? (
              <span>Zone {currentJob.zoneLabel} - {currentJob.deliveries} {currentJob.deliveryType === 'apartments' ? 'doors' : 'houses'}</span>
            ) : (
              t('track_delivery_zone')
            )}
          </p>
        </div>

        {/* GPS status indicator */}
        <div className="absolute top-3 right-3 z-[1000]">
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
            isTracking && gpsSignalStrength !== 'error'
              ? insideBoundary 
                ? 'bg-green-500/80 text-white'
                : 'bg-yellow-500/80 text-white'
              : gpsSignalStrength === 'error'
              ? 'bg-red-500/80 text-white'
              : 'bg-gray-700/80 text-gray-300'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isTracking && gpsSignalStrength !== 'error' ? 'bg-white animate-pulse' : 'bg-gray-400'
            }`} />
            {isTracking ? (gpsSignalStrength === 'error' ? 'GPS Lost' : insideBoundary ? 'In Zone' : 'Outside Zone') : 'Ready'}
          </div>
          {/* Background tracking indicator */}
          {usingBackgroundTracking && isTracking && (
            <div className="mt-1 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-500/80 text-white" data-testid="background-tracking-indicator">
              <Smartphone size={10} />
              Background Active
            </div>
          )}
          {/* Offline indicator on map */}
          {!online && (
            <div className="mt-1 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/80 text-white" data-testid="map-offline-indicator">
              <WifiOff size={10} />
              Offline
            </div>
          )}
        </div>

        {/* Map overlay stats */}
        <div className="absolute bottom-3 left-3 right-3 flex gap-2 z-[1000]">
          <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex-1">
            <p className="text-[10px] text-gray-400">{t('track_distance')}</p>
            <p className="text-lg font-bold text-white">{distance.toFixed(2)} mi</p>
          </div>
          <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex-1">
            <p className="text-[10px] text-gray-400">{t('track_duration')}</p>
            <p className="text-lg font-bold text-white">{formatTime(duration)}</p>
          </div>
          <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
            <p className="text-[10px] text-gray-400">{t('track_points')}</p>
            <p className="text-lg font-bold text-white">{routePath.length}{!online && offlineGPSBuffer.length > 0 ? ` (+${offlineGPSBuffer.length})` : ''}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-5 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-white/10 backdrop-blur-sm border-0 p-4 text-center">
            <Package size={22} className="mx-auto text-orange-400 mb-1" />
            <p className="text-3xl font-bold">{deliveries}</p>
            <p className="text-xs text-gray-400 mt-1">{t('track_deliveries')}</p>
          </Card>
          <Card className="bg-white/10 backdrop-blur-sm border-0 p-4 text-center">
            <DollarSign size={22} className="mx-auto text-green-400 mb-1" />
            <p className="text-3xl font-bold">${earnings.toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-1">{t('track_earned')}</p>
          </Card>
        </div>
      </div>

      {/* GPS Front Door Detection Toggle - Only for Houses and Apartments */}
      {usesFrontDoorDetection && (
        <div className="px-5 mb-3">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-white flex items-center gap-2">
                  <Zap size={16} className="text-yellow-400" />
                  GPS Front Door Detection
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Deliveries credited when GPS shows you at a front door for {STOP_DURATION_SECONDS} seconds
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => {
                    setFrontDoorDetectionEnabled(!frontDoorDetectionEnabled);
                    toast.info(frontDoorDetectionEnabled ? 'Front door detection disabled' : 'Front door detection enabled');
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors cursor-pointer ${
                    frontDoorDetectionEnabled 
                      ? 'bg-green-500' 
                      : 'bg-gray-600'
                  }`}
                  data-testid="gps-detection-toggle"
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                    frontDoorDetectionEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Manual Mode Toggle - Available for ALL users */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-purple-500/30 mt-3" data-testid="manual-mode-toggle-section">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-white flex items-center gap-2">
                  <Hand size={16} className="text-purple-400" />
                  Manual Mode
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Tap button for each delivery instead of automatic GPS detection
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => {
                    setManualTrackingMode(!manualTrackingMode);
                    if (!manualTrackingMode) {
                      toast.success('Manual mode enabled - tap to count each delivery');
                    } else {
                      toast.info('Manual mode disabled - using GPS detection');
                    }
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors cursor-pointer ${
                    manualTrackingMode 
                      ? 'bg-purple-500' 
                      : 'bg-gray-600'
                  }`}
                  data-testid="manual-mode-toggle"
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                    manualTrackingMode ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Delivery Detection Status - Only for Houses and Apartments */}
      {isTracking && usesFrontDoorDetection && frontDoorDetectionEnabled && (
        <div className="px-5 mb-3">
          <div className={`rounded-xl p-3 flex items-center gap-3 ${
            nearbyHouse 
              ? 'bg-yellow-500/20 border border-yellow-500/50' 
              : houseLocations.length > 0 
                ? 'bg-blue-500/10 border border-blue-500/30'
                : 'bg-gray-500/10 border border-gray-500/30'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              nearbyHouse ? 'bg-yellow-500/30' : 'bg-blue-500/20'
            }`}>
              {nearbyHouse ? (
                <div className="relative">
                  <Home size={20} className="text-yellow-400" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-[10px] font-bold text-black">{stopTimer}</span>
                  </div>
                </div>
              ) : loadingHouses ? (
                <div className="animate-spin w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full" />
              ) : (
                <Zap size={20} className="text-blue-400" />
              )}
            </div>
            <div className="flex-1">
              {nearbyHouse ? (
                <>
                  <p className="text-sm font-medium text-yellow-400">
                    {t('track_at_front_door')} {nearbyHouse.address ? `#${nearbyHouse.address}` : ''} {nearbyHouse.street || ''}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t('track_stay_seconds').replace('{seconds}', STOP_DURATION_SECONDS - stopTimer)}
                  </p>
                </>
              ) : loadingHouses ? (
                <>
                  <p className="text-sm font-medium text-blue-400">{t('track_loading_houses')}</p>
                  <p className="text-xs text-gray-400">{t('track_fetching_coords')}</p>
                </>
              ) : houseLocations.length > 0 ? (
                <>
                  <p className="text-sm font-medium text-blue-400">{t('track_front_door_active')}</p>
                  <p className="text-xs text-gray-400">
                    {houseLocations.length} {t('track_houses_count')} • {deliveredHouses.size} {t('track_delivered')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-400">{t('track_no_houses')}</p>
                  <p className="text-xs text-gray-500">{t('track_no_houses_desc')}</p>
                </>
              )}
            </div>
            {nearbyHouse && (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full border-4 border-yellow-500/50 flex items-center justify-center relative overflow-hidden">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-yellow-500 transition-all duration-1000"
                    style={{ height: `${(stopTimer / STOP_DURATION_SECONDS) * 100}%` }}
                  />
                  <span className="relative text-lg font-bold text-yellow-400">{stopTimer}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hourly Pay Status - For Cars and Hands */}
      {isTracking && isPaidHourly && (
        <div className="px-5 mb-3">
          <div className="rounded-xl p-3 flex items-center gap-3 bg-green-500/10 border border-green-500/30">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500/20">
              <Clock size={20} className="text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-400">{t('track_hourly_active')}</p>
              <p className="text-xs text-gray-400">
                ${hourlyRate.toFixed(2)}/{t('track_hourly').toLowerCase()} • {formatTime(duration)} {t('track_worked')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-green-400">${earnings.toFixed(2)}</p>
              <p className="text-xs text-gray-500">{t('track_earned').toLowerCase()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      <div className="px-5 mt-auto pb-24">
        {!isTracking ? (
          <>
            <button
              data-testid="start-delivering-btn"
              onClick={handleStart}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-5 rounded-2xl font-bold text-xl shadow-lg shadow-orange-500/30 flex items-center justify-center gap-3 transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Play size={28} fill="white" />
              {t('track_start_delivering')}
            </button>

            {/* Delivery Type Selector */}
            <div className="mt-5">
              <p className="text-center text-gray-400 text-sm mb-3">
                {currentJob?.id && currentJob.id !== 'demo' 
                  ? t('track_delivery_type') + ' (Locked)'
                  : t('track_delivery_type')}
              </p>
              <div className="grid grid-cols-4 gap-2" data-testid="delivery-type-selector">
                {deliveryTypes.map((type) => {
                  const Icon = type.icon;
                  const isHourly = type.payType === 'hourly';
                  const isLocked = currentJob?.id && currentJob.id !== 'demo';
                  const isSelected = selectedDeliveryType === type.id;
                  return (
                    <button
                      key={type.id}
                      data-testid={`delivery-type-${type.id}`}
                      onClick={() => {
                        // Only allow changing if no active job
                        if (!isLocked) {
                          setSelectedDeliveryType(type.id);
                        }
                      }}
                      disabled={isLocked && !isSelected}
                      className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl text-xs font-medium transition-all relative ${
                        isSelected
                          ? 'bg-orange-500 text-white'
                          : isLocked
                            ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      <Icon size={20} />
                      <span>{t(type.nameKey)}</span>
                      <span className={`text-[9px] ${
                        isSelected 
                          ? 'text-orange-100' 
                          : isHourly ? 'text-green-400' : 'text-blue-400'
                      }`}>
                        {isHourly ? t('track_hourly') : t('track_per_flyer')}
                      </span>
                      {isLocked && isSelected && (
                        <Lock size={10} className="absolute top-1 right-1 text-orange-200" />
                      )}
                    </button>
                  );
                })}
              </div>
              {currentJob?.id && currentJob.id !== 'demo' && (
                <p className="text-center text-xs text-gray-500 mt-2">
                  Delivery type is set by your accepted job
                </p>
              )}
            </div>

            {/* Pay Type Info - Changes based on selected delivery type */}
            {usesFrontDoorDetection ? (
              <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <Zap size={18} className="text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-blue-400">{t('track_gps_detection')}</p>
                    <p className="text-xs text-gray-400">{t('track_gps_detection_desc')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-green-400">{t('track_hourly_pay')} - ${hourlyRate.toFixed(2)}/hr</p>
                    <p className="text-xs text-gray-400">{t('track_hourly_pay_desc')} {t(deliveryTypes.find(d => d.id === selectedDeliveryType)?.nameKey).toLowerCase()}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-4">
            <button
              data-testid="finish-btn"
              onClick={handleStop}
              className="flex-1 bg-red-500/20 border border-red-500/50 text-red-400 py-4 rounded-2xl font-semibold flex items-center justify-center gap-2"
            >
              <Square size={20} fill="currentColor" />
              {t('track_finish')}
            </button>
            <button
              data-testid="pause-resume-btn"
              onClick={handlePause}
              className={`flex-1 py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 ${
                isPaused
                  ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                  : 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-400'
              }`}
            >
              {isPaused ? (
                <><Play size={20} fill="currentColor" /> {t('track_resume')}</>
              ) : (
                <><Pause size={20} /> {t('track_pause')}</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Complete Job Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end justify-center">
          <div className="bg-gray-800 w-full max-w-[430px] rounded-t-3xl p-6 animate-slide-up">
            <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-6" />
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('track_complete_session')}</h3>
              <p className="text-gray-400 text-sm">{t('track_great_work')}</p>
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_flyers_delivered')}</span>
                <span className="font-bold text-orange-400">{deliveries}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_total_distance')}</span>
                <span className="font-bold">{distance.toFixed(2)} mi</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_session_time')}</span>
                <span className="font-bold">{formatTime(duration)}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_total_earnings')}</span>
                <span className="font-bold text-green-400">${earnings.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={handleConfirmComplete}
              disabled={saving}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-xl font-semibold mb-3 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-complete-btn"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('track_save_complete')
              )}
            </button>
            <button
              onClick={() => setShowCompleteModal(false)}
              disabled={saving}
              className="w-full text-gray-400 py-3 disabled:opacity-50"
              data-testid="continue-delivering-btn"
            >
              {t('track_continue')}
            </button>
          </div>
        </div>
      )}

      {/* Success Modal with Request Payout */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 overflow-y-auto py-4">
          <div className="bg-gray-800 w-full max-w-sm rounded-2xl p-6 animate-slide-up my-auto">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={36} className="text-green-400" />
              </div>
              <h3 className="text-xl font-bold mb-1 text-white">{t('track_job_completed') || 'Job Completed!'}</h3>
              <p className="text-gray-400 text-sm">{t('track_great_work')}</p>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_flyers_delivered')}</span>
                <span className="font-bold text-orange-400">{deliveries}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                <span className="text-sm text-gray-300">{t('track_total_earnings')}</span>
                <span className="font-bold text-green-400">${earnings.toFixed(2)}</span>
              </div>
            </div>

            {/* Photo Upload Section */}
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Camera size={18} className="text-blue-400" />
                  <span className="text-sm font-medium text-white">{t('photo_upload_title') || 'Upload Delivery Photos'}</span>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  uploadedPhotos.length >= REQUIRED_PHOTOS 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-orange-500/20 text-orange-400'
                }`}>
                  {uploadedPhotos.length}/{REQUIRED_PHOTOS}
                </span>
              </div>
              
              <p className="text-xs text-gray-400 mb-3">
                {t('photo_upload_desc') || 'Upload 10 photos of flyers on doors for client reporting'}
              </p>
              
              {/* Photo Grid */}
              {uploadedPhotos.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {uploadedPhotos.map((photo, idx) => (
                    <div key={photo.id || idx} className="aspect-square rounded-lg overflow-hidden bg-gray-700">
                      <img 
                        src={photo.thumbnail || photo.url} 
                        alt={`Delivery ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {/* Empty slots */}
                  {Array.from({ length: Math.min(REQUIRED_PHOTOS - uploadedPhotos.length, 5 - (uploadedPhotos.length % 5 || 5)) }).map((_, idx) => (
                    <div key={`empty-${idx}`} className="aspect-square rounded-lg bg-gray-700/50 border-2 border-dashed border-gray-600 flex items-center justify-center">
                      <Image size={16} className="text-gray-500" />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload Button */}
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              {uploadedPhotos.length < REQUIRED_PHOTOS && (
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="upload-photos-btn"
                >
                  {uploadingPhoto ? (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      {t('uploading') || 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      {t('photo_upload_btn') || `Upload Photos (${REQUIRED_PHOTOS - uploadedPhotos.length} remaining)`}
                    </>
                  )}
                </button>
              )}
              
              {uploadedPhotos.length >= REQUIRED_PHOTOS && (
                <div className="flex items-center justify-center gap-2 text-green-400 py-2">
                  <CheckCircle size={18} />
                  <span className="text-sm font-medium">{t('photos_complete') || 'All photos uploaded!'}</span>
                </div>
              )}
            </div>

            {/* PayPal status check */}
            {!user?.paypal_email && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-400">{t('payout_no_paypal') || 'No PayPal Connected'}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('payout_add_paypal_message') || 'Add your PayPal email in Profile to receive payments.'}</p>
                </div>
              </div>
            )}

            <button
              onClick={handleRequestPayout}
              disabled={uploadedPhotos.length < REQUIRED_PHOTOS}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl font-semibold mb-3 flex items-center justify-center gap-2 shadow-lg shadow-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="request-payout-btn"
            >
              <CreditCard size={20} />
              {t('payout_request') || 'Request Payout'}
            </button>
            
            <button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/history');
              }}
              className="w-full text-gray-400 py-3"
              data-testid="skip-payout-btn"
            >
              {t('payout_skip') || 'Skip for Now'}
            </button>
          </div>
        </div>
      )}

      {/* Incomplete Job Warning Modal */}
      {showIncompleteWarning && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm border border-red-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">INCOMPLETE JOB</h3>
              <h4 className="text-lg font-semibold text-red-400 mb-3">ARE YOU SURE?</h4>
              <p className="text-gray-400 text-sm">
                You have only completed <span className="text-white font-bold">{deliveries}</span> of{' '}
                <span className="text-white font-bold">{currentJob?.deliveries || 0}</span> deliveries.
              </p>
              <p className="text-orange-400 text-sm mt-3 font-medium">
                Incomplete jobs may result in a lower work rating.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => setShowIncompleteWarning(false)}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition-colors"
                data-testid="continue-delivery-btn"
              >
                Continue Delivering
              </button>
              <button
                onClick={handleIncompleteConfirm}
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 py-3 rounded-xl font-semibold transition-colors border border-red-500/30"
                data-testid="finish-incomplete-btn"
              >
                Finish Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial Delivery Message Modal */}
      {showPartialDeliveryMsg && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm border border-orange-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-500/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">PARTIAL DELIVERY</h3>
              <p className="text-gray-400 text-sm mb-4">
                Your delivery has been marked as incomplete.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <p className="text-orange-400 font-semibold">Contact Support</p>
                <p className="text-gray-400 text-xs mt-1">
                  Please contact support to report the reason for the incomplete delivery.
                </p>
              </div>
            </div>
            
            <button
              onClick={handlePartialDeliveryAcknowledge}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold transition-colors"
              data-testid="acknowledge-partial-btn"
            >
              I Understand - Continue
            </button>
          </div>
        </div>
      )}

      {/* Out of Zone Warning Modal */}
      {showOutOfZoneWarning && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm border border-yellow-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full mx-auto flex items-center justify-center mb-4 animate-pulse">
                <AlertCircle size={32} className="text-yellow-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">⚠️ WRONG DELIVERY ZONE</h3>
              <p className="text-gray-300 text-sm mb-3">
                You appear to be outside your assigned delivery area.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-3">
                <p className="text-yellow-400 font-semibold text-sm">
                  📍 Please return to your assigned zone
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Deliveries made outside your zone may not be counted.
                </p>
              </div>
              <p className="text-gray-500 text-xs">
                Job: {currentJob?.title || 'Unknown'}
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowOutOfZoneWarning(false);
                  // Center map on delivery zone
                  if (currentJob?.mapCenter) {
                    setMapCenter(currentJob.mapCenter);
                  }
                }}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black py-3 rounded-xl font-semibold transition-colors"
                data-testid="return-to-zone-btn"
              >
                <Navigation size={18} className="inline mr-2" />
                Show My Delivery Zone
              </button>
              <button
                onClick={() => setShowOutOfZoneWarning(false)}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-semibold transition-colors"
                data-testid="dismiss-zone-warning-btn"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Session Modal */}
      {showResumeSessionModal && savedSessionData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm border border-blue-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <RefreshCw size={32} className="text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Resume Previous Session?</h3>
              <p className="text-gray-300 text-sm mb-4">
                We found a saved delivery session from your last visit.
              </p>
              
              <div className="bg-gray-800 rounded-xl p-4 mb-4 text-left">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-400">Deliveries</p>
                    <p className="text-white font-bold text-lg">{savedSessionData.deliveries || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Duration</p>
                    <p className="text-white font-bold text-lg">
                      {Math.floor((savedSessionData.duration || 0) / 60)}:{String((savedSessionData.duration || 0) % 60).padStart(2, '0')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Distance</p>
                    <p className="text-white font-bold">{(savedSessionData.distance || 0).toFixed(2)} mi</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Saved</p>
                    <p className="text-gray-300 text-xs">
                      {savedSessionData.savedAt ? new Date(savedSessionData.savedAt).toLocaleTimeString() : 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleResumeSession}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="resume-session-btn"
              >
                <Play size={18} />
                Resume Session
              </button>
              <button
                onClick={handleDiscardSession}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-semibold transition-colors"
                data-testid="start-fresh-btn"
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackPage;
